import React, { useState, useEffect } from 'react';
import { User } from '../types/User';
import { X, Check, Calendar, Clock, MapPin, User as UserIcon, Trash2 } from 'lucide-react';
import { MeetingService } from '../services/meetingService';
import CustomNotification from './CustomNotification';

interface MeetingNotificationsProps {
  currentUser: User;
  onMeetingUpdate?: () => void;
}

const MeetingNotifications: React.FC<MeetingNotificationsProps> = ({ currentUser, onMeetingUpdate }) => {
  const [pendingRequests, setPendingRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({ show: false, message: '', type: 'info' });

  useEffect(() => {
    loadPendingRequests();
    // Pending-request polling is paused while the project is under load.
  }, [currentUser.id]);

  const loadPendingRequests = async () => {
    const requests = await MeetingService.getPendingMeetingRequests(currentUser.id);
    setPendingRequests(requests);
    setLoading(false);
  };

  const handleAcceptMeeting = async (meetingId: string) => {
    const result = await MeetingService.updateMeetingStatus(meetingId, 'confirmed');
    if (result.success) {
      setNotification({
        show: true,
        message: 'Meeting confirmed! You will receive table assignment details.',
        type: 'success'
      });
      // Force refresh the pending requests list
      await loadPendingRequests();
      onMeetingUpdate?.();
    } else {
      setNotification({
        show: true,
        message: 'Failed to confirm meeting: ' + result.error,
        type: 'error'
      });
    }
  };

  const handleRejectMeeting = async (meetingId: string) => {
    const result = await MeetingService.updateMeetingStatus(meetingId, 'rejected');
    if (result.success) {
      // Force refresh the pending requests list
      await loadPendingRequests();
    } else {
      setNotification({
        show: true,
        message: 'Failed to reject meeting: ' + result.error,
        type: 'error'
      });
    }
  };

  const handleCancelMeeting = async (meetingId: string) => {
    const meetingToCancel = pendingRequests.find(req => req.id === meetingId);
    const result = await MeetingService.deleteMeeting(meetingId, currentUser.id);
    if (result.success) {
      setNotification({
        show: true,
        message: `Meeting with ${meetingToCancel?.requester.name} has been cancelled and slot freed up`,
        type: 'info'
      });
      // Force refresh the pending requests list
      await loadPendingRequests();
      // Notify parent component about the update
      onMeetingUpdate?.();
    } else {
      setNotification({
        show: true,
        message: 'Failed to cancel meeting: ' + result.error,
        type: 'error'
      });
    }
  };

  if (loading || pendingRequests.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-16 left-3 right-3 lg:top-4 lg:left-auto lg:right-4 z-50 space-y-3 lg:w-96">
      {pendingRequests.map((request) => (
        <div key={request.id} className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-full relative">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5" style={{ color: '#C89116' }} />
              <span className="font-semibold text-gray-800">Meeting Request</span>
            </div>
          </div>
          
          <div className="space-y-2 mb-4">
            <div className="flex items-center space-x-2">
              <UserIcon className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">
                From: <strong>{request.requester.name}</strong>
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">
                {request.meeting_date} at {request.time_slot}
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <MapPin className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">
                Table {request.table_number}
              </span>
            </div>
            
            <div className="text-sm text-gray-600 mt-2">
              <strong>Subject:</strong> {request.subject}
            </div>
            
            {request.description && (
              <div className="text-sm text-gray-600">
                <strong>Description:</strong> {request.description}
              </div>
            )}
          </div>
          
          <div className="flex space-x-2 relative">
            <button
              onClick={() => handleAcceptMeeting(request.id)}
              className="flex-1 flex items-center justify-center space-x-1 px-2 py-2 text-white rounded-md hover:opacity-90 transition-colors text-sm"
              style={{ backgroundColor: '#C89116' }}
            >
              <Check className="w-4 h-4" />
              <span>Accept</span>
            </button>
            
            <button
              onClick={() => handleRejectMeeting(request.id)}
              className="flex-1 flex items-center justify-center space-x-1 px-2 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm"
            >
              <X className="w-4 h-4" />
              <span>Reject</span>
            </button>
          </div>
          
        </div>
      ))}

      {/* Custom Notification */}
      {notification.show && (
        <CustomNotification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification({ show: false, message: '', type: 'info' })}
        />
      )}
    </div>
  );
};

export default MeetingNotifications;